import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace, useDecks, useCards, useStorage } from '../hooks/useApi';
import { saveOrShareFile } from '../lib/fileUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, MoreVertical, Trash2, Edit, ArrowLeft, FileDown, 
  Search, Layers, FolderPlus, ExternalLink
} from 'lucide-react';

export function DeckManager() {
  const navigate = useNavigate();
  const storage = useStorage();
  const { currentWorkspace } = useWorkspace();
  const { cards } = useCards();
  const { decks, loading, refetch } = useDecks();

  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingDeck, setEditingDeck] = useState(null);
  const [deleteDeck, setDeleteDeck] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create deck state
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [cardSearch, setCardSearch] = useState('');
  const [creating, setCreating] = useState(false);

  // Filter decks by search
  const filteredDecks = useMemo(() => {
    if (!search.trim()) return decks;
    const q = search.toLowerCase();
    return decks.filter(d => 
      d.name.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q)
    );
  }, [decks, search]);

  // Filter cards for selection
  const filteredCards = useMemo(() => {
    if (!cardSearch.trim()) return cards;
    const q = cardSearch.toLowerCase();
    return cards.filter(c => c.title.toLowerCase().includes(q));
  }, [cards, cardSearch]);

  const toggleCard = (cardId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Deck name is required');
      return;
    }

    setCreating(true);
    try {
      const result = await storage.createDeck(newName.trim(), newDescription.trim(), Array.from(selectedCards));
      toast.success(`Deck created with ${result.total_cards_after_closure} cards (${result.auto_included} auto-included for closure)`);
      refetch();
      setShowCreate(false);
      setNewName('');
      setNewDescription('');
      setSelectedCards(new Set());
    } catch (error) {
      toast.error(error.message || 'Failed to create deck');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingDeck || !newName.trim()) return;

    try {
      const deck = { ...editingDeck, name: newName.trim(), description: newDescription.trim() };
      await storage.saveDeck(deck);
      toast.success('Deck updated');
      refetch();
      setEditingDeck(null);
      setNewName('');
      setNewDescription('');
    } catch (error) {
      toast.error(error.message || 'Failed to update deck');
    }
  };

  const handleDelete = async () => {
    if (!deleteDeck) return;
    setIsDeleting(true);

    try {
      await storage.deleteDeck(deleteDeck.id);
      toast.success('Deck deleted');
      refetch();
      setDeleteDeck(null);
    } catch (error) {
      toast.error(error.message || 'Failed to delete deck');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async (deck) => {
    try {
      const data = await storage.exportDeck(deck.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      await saveOrShareFile(blob, `${deck.name}.json`, 'application/json');
      toast.success('Deck exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const openEditDialog = (deck) => {
    setEditingDeck(deck);
    setNewName(deck.name);
    setNewDescription(deck.description || '');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="deck-manager">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <h1 className="text-xl font-bold">Decks</h1>
              <Badge variant="outline">{currentWorkspace?.name}</Badge>
            </div>
            <Button onClick={() => setShowCreate(true)} data-testid="new-deck-btn">
              <Plus className="h-4 w-4 mr-2" /> New Deck
            </Button>
          </div>
          
          {/* Search */}
          <div className="mt-3 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search decks..."
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Decks Grid */}
      <main className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : filteredDecks.length === 0 ? (
          <div className="text-center py-12">
            <Layers className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              {search ? 'No decks match your search' : 'No decks yet'}
            </p>
            <Button onClick={() => setShowCreate(true)}>
              <FolderPlus className="h-4 w-4 mr-2" /> Create Your First Deck
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredDecks.map(deck => (
              <Card key={deck.id} className="group">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{deck.name}</CardTitle>
                      {deck.description && (
                        <CardDescription className="line-clamp-2">{deck.description}</CardDescription>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/?deck=${deck.id}`)}>
                          <ExternalLink className="h-4 w-4 mr-2" /> View Cards
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditDialog(deck)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport(deck)}>
                          <FileDown className="h-4 w-4 mr-2" /> Export
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setDeleteDeck(deck)} className="text-destructive">
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary">{deck.cards?.length || 0} cards</Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/?deck=${deck.id}`)}
                    >
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Deck Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Deck</DialogTitle>
            <DialogDescription>
              Select seed cards and the system will auto-expand to include all related cards for a closed graph.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Deck Name *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="My Deck" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Optional description" />
            </div>
            <div>
              <Label className="flex items-center justify-between">
                <span>Select Seed Cards ({selectedCards.size} selected)</span>
              </Label>
              <Input 
                value={cardSearch} 
                onChange={(e) => setCardSearch(e.target.value)}
                placeholder="Search cards..."
                className="mt-2"
              />
              <ScrollArea className="h-64 mt-2 border rounded-md p-2">
                {filteredCards.map(card => (
                  <div key={card.id} className="flex items-center gap-2 py-2 px-2 hover:bg-muted/50 rounded">
                    <Checkbox 
                      checked={selectedCards.has(card.id)}
                      onCheckedChange={() => toggleCard(card.id)}
                    />
                    <span className="flex-1">{card.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {Object.keys(card.fields || {}).length} fields
                    </span>
                  </div>
                ))}
                {filteredCards.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No cards found</p>
                )}
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating...' : `Create Deck (${selectedCards.size} seed cards)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Deck Dialog */}
      <Dialog open={!!editingDeck} onOpenChange={() => setEditingDeck(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Deck</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Deck Name *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="My Deck" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Optional description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDeck(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDeck} onOpenChange={() => setDeleteDeck(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deck</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDeck?.name}"? The cards will remain in your workspace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Deck'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default DeckManager;
